import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import {
  Row, Col, Label, Card, CardHeader, CardBody,
  Table, Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Input, InputGroup, InputGroupText, FormText
} from 'reactstrap';
import { apiService } from '../../constants/ApiService';
import { useSelector } from 'react-redux';
import useVendorBranch from '../../utils/useVendorBranch';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { toast } from 'react-toastify';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import { formatDate } from '../../constants/DateFormatter';
import { Link } from 'react-router-dom';

// ─── helpers ────────────────────────────────────────────────────────────────
const extractList = (res) => {
  const c = res?.data ?? res;
  return (
    (Array.isArray(c?.data) && c.data) ||
    (Array.isArray(c?.Data) && c.Data) ||
    (Array.isArray(c) && c) ||
    []
  );
};

const fmt = (n) =>
  Number(n ?? 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

// ─── component ──────────────────────────────────────────────────────────────
const FeeVerification = () => {
  // filter state
  const [globalSearch, setGlobalSearch] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSessionYear, setSelectedSessionYear] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [verifyFilter, setVerifyFilter] = useState(''); // '' | 'verified' | 'pending'
  const [feesTypeFilter, setFeesTypeFilter] = useState(''); // '' | 'COURSE_FEES' | 'HOSTEL_FEES'

  const [errorMessage, setErrorMessage] = useState({});

  const { user } = useSelector((state) => state.authState || {});
  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  // option lists
  const [classOptions, setClassOptions] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([]);

  // table data
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);

  // totals
  const [totalCollection, setTotalCollection] = useState(0);
  const [cashCollection, setCashCollection] = useState(0);
  const [bankCollection, setBankCollection] = useState(0);

  // remark modal
  const [remarkModal, setRemarkModal] = useState(false);
  const [activeReceipt, setActiveReceipt] = useState(null);

  const remarkOptions = [
    { value: 'VERIFIED_CORRECT', label: 'Verified – All details are correct' },
    { value: 'AMOUNT_MISMATCH', label: 'Amount mismatch' },
    { value: 'DATE_MISMATCH', label: 'Date mismatch' },
    { value: 'STUDENT_MISMATCH', label: 'Student name / ID mismatch' },
    { value: 'RECEIPT_NO_MISMATCH', label: 'Receipt no. mismatch' },
    { value: 'PAYMENT_MODE_MISMATCH', label: 'Payment mode mismatch' },
    { value: 'DUPLICATE_RECEIPT', label: 'Possible duplicate receipt' },
    { value: 'INCOMPLETE_DATA', label: 'Incomplete data' },
    { value: 'INCORRECT_DATA', label: 'Incorrect data' },
    { value: 'REFUND_INITIATED', label: 'Refund initiated' },
    { value: 'CUSTOM', label: 'Other / Custom Remark' },
  ];

  const [selectedRemark, setSelectedRemark] = useState('');
  const [remarkText, setRemarkText] = useState('');

  // ── fetch helpers ──────────────────────────────────────────────────────────
  const fetchClasses = useCallback(async (search = '') => {
    try {
      const res = await apiService.get(
        `api/ClassMaster/GetClassMaster?vendorId=${selectionData?.vendor_id || ''}&branchId=${selectionData?.branch_id || ''}&searchItem=${encodeURIComponent(search)}`
      );
      const list = extractList(res);
      setClassOptions(
        list
          .map((x) => ({ value: x.id, label: x.class_Name }))
          .filter((o) => o.value && o.label)
      );
    } catch { setClassOptions([]); }
  }, [selectionData]);

  const fetchSessions = useCallback(async (search = '') => {
    try {
      const res = await apiService.get(
        `api/SessionMaster/GetAllSession?vendorId=${selectionData?.vendor_id || ''}&branchId=${selectionData?.branch_id || ''}&search=${encodeURIComponent(search)}`
      );
      const list = extractList(res);
      const opts = list.map((x) => {
        const name = x.session_name ?? x.sessionName ?? x.session_year ?? x.sessionYear ?? '';
        const s = String(name).trim();
        return s ? { value: s, label: s } : null;
      }).filter(Boolean);
      setSessionOptions(Array.from(new Map(opts.map((o) => [o.value, o])).values()));
    } catch { setSessionOptions([]); }
  }, [selectionData]);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (globalSearch) queryParams.append('search', globalSearch);
      if (selectedClassId) queryParams.append('classId', selectedClassId);
      if (selectedSessionYear) queryParams.append('session', selectedSessionYear);
      if (fromDate) queryParams.append('fromDate', fromDate);
      if (toDate) queryParams.append('toDate', toDate);

      const response = await apiService.get(
        `api/FeeReciept/GetFeeReciept?vendorId=${selectionData?.vendor_id || ''}&branchId=${selectionData?.branch_id || ''}&${queryParams.toString()}`
      );

      if (response.status === true) {
        setReceipts(response.data ?? []);
        setTotalCollection(response.totalCollection ?? 0);
        setCashCollection(response.cashCollection ?? 0);
        setBankCollection(response.bankCollection ?? 0);
      } else {
        setReceipts([]);
        setTotalCollection(0);
        setCashCollection(0);
        setBankCollection(0);
      }
    } catch {
      setReceipts([]);
      setTotalCollection(0);
      setCashCollection(0);
      setBankCollection(0);
    } finally { setLoading(false); }
  }, [globalSearch, selectedClassId, selectedSessionYear, fromDate, toDate, selectionData]);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });
    if (canLoad) {
      fetchReceipts();
      fetchClasses();
    }
  }, [globalSearch, selectedClassId, selectedSessionYear, fromDate, toDate, selectionData]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // ── derived counts ─────────────────────────────────────────────────────────
  const displayedReceipts = receipts.filter((r) => {
    if (verifyFilter === 'verified' && r.verified !== 1) return false;
    if (verifyFilter === 'pending' && r.verified === 1) return false;
    if (feesTypeFilter && (r.fee_head_name ?? '') !== feesTypeFilter) return false;
    return true;
  });
  const verifiedCount = receipts.filter((r) => r.verified === 1).length;
  const unverifiedCount = receipts.length - verifiedCount;

  // recompute totals from the filtered rows so cards stay in sync with active filters
  const displayedTotal = displayedReceipts.reduce((sum, r) => sum + Number(r.amt_rec ?? r.total_amount ?? 0), 0);
  const displayedCash  = displayedReceipts.filter((r) => r.modeofpayment?.toLowerCase() === 'cash').reduce((sum, r) => sum + Number(r.amt_rec ?? r.total_amount ?? 0), 0);
  const displayedBank  = displayedReceipts.filter((r) => r.modeofpayment?.toLowerCase() !== 'cash').reduce((sum, r) => sum + Number(r.amt_rec ?? r.total_amount ?? 0), 0);

  // ── remark modal helpers ───────────────────────────────────────────────────
  const openRemark = (receipt) => {
    setActiveReceipt(receipt);
    setSelectedRemark(receipt.verification_code ?? '');
    setRemarkText(receipt.verify_remark ?? '');
    setRemarkModal(true);
  };

  const saveRemark = async () => {
    if (!activeReceipt) return;
    const nextVerified = activeReceipt.verified === 1 ? 0 : 1;
    const payload = {
      verified: nextVerified,
      id: activeReceipt.id,
      verify_remark: remarkText,
      verification_code: selectedRemark,
    };
    try {
      await apiService.put(`api/FeeReciept/VerifyFees`, payload);
      await fetchReceipts();
    } catch (err) { console.error(err); }
    setRemarkModal(false);
    setActiveReceipt(null);
    setRemarkText('');
    setSelectedRemark('');
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />
      <div className='text-right mb-2'>
        <Link target='_blank' className='btn btn-primary' to="/save-fee-reciept">
          <i className='bi bi-plus-circle' /> New Fee Receipt
        </Link>
      </div>

      {/* Page header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0 font-weight-bold text-primary">
            <i className="bi bi-receipt mr-2" />
            Fee Transaction Verification
          </h5>
          <small className="text-muted">Review and verify fee receipt records</small>
        </div>
      </div>

      {/* Filter card */}
      <Card className="mb-3 border">
        <CardHeader className="bg-light py-2 px-3">
          <span className="font-weight-bold text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '0.5px' }}>
            <i className="bi bi-filter mr-2 text-muted" />
            Search &amp; Filter
          </span>
        </CardHeader>
        <CardBody className="py-3">
          {/* Global search */}
          <Row className="mb-3">
            <Col>
              <InputGroup>
                <InputGroupText>
                  <i className="bi bi-search text-muted" />
                </InputGroupText>
                <Input
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  placeholder="Search by student name, receipt no, enrollment no..."
                />
              </InputGroup>
            </Col>
          </Row>

          {/* Dropdown + date filters */}
          <Row className="mb-3">
            {/* Course */}
            <Col md="8">
              <Label className="mb-1 small font-weight-bold text-secondary">Course</Label>
              <Select
                options={classOptions}
                value={classOptions.find((o) => String(o.value) === String(selectedClassId)) || null}
                onChange={(opt) => setSelectedClassId(opt ? String(opt.value) : '')}
                onInputChange={(val, meta) => { if (meta.action === 'input-change') fetchClasses(val); }}
                isClearable
                isSearchable
                placeholder="Choose a Course..."
              />
            </Col>

            {/* Session */}
            <Col md="4">
              <Label className="mb-1 small font-weight-bold text-secondary">Session</Label>
              <Select
                options={sessionOptions}
                value={sessionOptions.find((o) => String(o.value) === String(selectedSessionYear)) || null}
                onChange={(opt) => setSelectedSessionYear(opt ? String(opt.value) : '')}
                onInputChange={(val, meta) => { if (meta.action === 'input-change') fetchSessions(val); }}
                isClearable
                isSearchable
                placeholder="Choose a Session..."
              />
            </Col>

            {/* From Date */}
            <Col md="2">
              <Label className="mb-1 small font-weight-bold text-secondary">From Date</Label>
              <Input
                type="date"
                bsSize='sm'
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Col>

            {/* To Date */}
            <Col md="2">
              <Label className="mb-1 small font-weight-bold text-secondary">To Date</Label>
              <Input
                type="date"
                bsSize='sm'
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Col>

         

          {/* Fees type filter */}
       
            <Col md="4">
              <Label className="mb-1 small font-weight-bold text-secondary">Fees Type</Label>
              <Select
                options={[
                  { value: '', label: 'All Types' },
                  { value: 'COURSE_FEES', label: 'Course Fees' },
                  { value: 'HOSTEL_FEES', label: 'Hostel Fees' },
                ]}
                value={
                  feesTypeFilter === 'COURSE_FEES' ? { value: 'COURSE_FEES', label: 'Course Fees' } :
                  feesTypeFilter === 'HOSTEL_FEES' ? { value: 'HOSTEL_FEES', label: 'Hostel Fees' } :
                  { value: '', label: 'All Types' }
                }
                onChange={(opt) => setFeesTypeFilter(opt?.value ?? '')}
                placeholder="All Types"
              />
            </Col>
            
            {/* Verification Status */}
            <Col md="4">
              <Label className="mb-1 small font-weight-bold text-secondary">Verification Status</Label>
              <Select
                options={[
                  { value: '', label: 'All Records' },
                  { value: 'verified', label: '✓ Verified only' },
                  { value: 'pending', label: '⏳ Pending only' },
                ]}
                value={
                  verifyFilter === 'verified' ? { value: 'verified', label: '✓ Verified only' } :
                  verifyFilter === 'pending' ? { value: 'pending', label: '⏳ Pending only' } :
                  { value: '', label: 'All Records' }
                }
                onChange={(opt) => setVerifyFilter(opt?.value ?? '')}
                placeholder="All Records"
              />
            </Col>
             </Row>
        </CardBody>
      </Card>

      {/* Collection summary strip */}
      <Row className="mb-3">
        <Col md="4">
          <Card className="border text-center py-2">
            <div className="text-muted small mb-1">Total Collection</div>
            <div className="font-weight-bold text-primary" style={{ fontSize: '1.1rem' }}>{fmt(displayedTotal)}</div>
          </Card>
        </Col>
        <Col md="4">
          <Card className="border text-center py-2">
            <div className="text-muted small mb-1">Cash Collection</div>
            <div className="font-weight-bold text-success" style={{ fontSize: '1.1rem' }}>{fmt(displayedCash)}</div>
          </Card>
        </Col>
        <Col md="4">
          <Card className="border text-center py-2">
            <div className="text-muted small mb-1">Bank / Online Collection</div>
            <div className="font-weight-bold text-info" style={{ fontSize: '1.1rem' }}>{fmt(displayedBank)}</div>
          </Card>
        </Col>
      </Row>

      {/* Table card */}
      <Card className="border">
        {/* Stats strip */}
        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light">
          <div className="d-flex align-items-center" style={{ gap: '16px' }}>
            <small className="text-success">
              <i className="bi bi-circle mr-1" style={{ fontSize: '8px' }} />
              {verifiedCount} Verified
            </small>
            <small className="text-warning">
              <i className="bi bi-circle mr-1" style={{ fontSize: '8px' }} />
              {unverifiedCount} Pending
            </small>
          </div>
          <small className="text-muted">{displayedReceipts.length} receipts</small>
        </div>

        <div className="table-responsive compact-table">
          <Table hover className="mb-0">
            <thead className="thead-light">
              <tr>
                <th>#</th>
                <th className="text-center">Actions</th>
                <th>Student</th>
                <th>Fees Type</th>
                <th>Transaction No.</th>
                <th className='text-right'>Amount</th>
                <th>Mode</th>
                <th>Course</th>
                <th width="10%">Date</th>
                <th className="text-center">Status</th>
                <th>Verified By</th>
                <th>Code</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="text-center py-5 text-muted">
                    <i className="bi bi-spinner fa-spin mr-2" />Loading...
                  </td>
                </tr>
              ) : displayedReceipts.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fa-2x d-block mb-2" />
                    No receipts found
                  </td>
                </tr>
              ) : (
                displayedReceipts.map((r, i) => (
                  <tr key={r.id ?? i}>
                    <td className="text-muted small">{i + 1}</td>

                    {/* Action buttons */}
                    <td className="text-center">
                      <div className="d-flex justify-content-center" style={{ gap: '6px' }}>
                        {r.verified === 1 ? (
                          <Button
                            className="btn-sm py-1 px-1"
                            outline
                            color="danger"
                            onClick={() => openRemark(r)}
                            title="Undo Verification"
                          >
                            <i className="bi bi-x-circle" /> Undo
                          </Button>
                        ) : (
                          <Button
                            className="btn-sm py-1 px-1"
                            outline
                            color="success"
                            onClick={() => openRemark(r)}
                            title="Verify"
                          >
                            <i className="bi bi-check-circle" /> Verify
                          </Button>
                        )}
                      </div>
                    </td>

                    {/* Student */}
                    <td className="font-weight-bold">
                      {r.sname1 ?? r.stud_name ?? '—'}
                      <br />
                      <small className="text-muted">{r.sadmitno ?? r.admit_no ?? ''}</small>
                    </td>

                    {/* Fees Type */}
                    <td>
                      {r.fee_head_name ? (
                        <Badge
                          color={r.fee_head_name === 'COURSE_FEES' ? 'dark' : 'info'}
                          pill
                          className="badge-sm py-1 text-white"
                        >
                          {r.fee_head_name}
                        </Badge>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>

                    {/* Receipt No */}
                    <td><code>{r.chq_no ?? '—'}</code></td>

                    {/* Amount */}
                    <td className="font-weight-bold text-right">
                      {fmt(r.amt_rec ?? r.total_amount)}
                    </td>

                    {/* Payment mode */}
                    <td>
                      {r.modeofpayment ? (
                        <Badge
                          color={r.modeofpayment?.toLowerCase() === 'cash' ? 'success' : 'info'}
                          pill
                          className="badge-sm py-1 text-white"
                        >
                          {r.modeofpayment}
                        </Badge>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>

                    {/* Course */}
                    <td><small>{r.curr_class ?? '—'}</small></td>

                    {/* Date */}
                    <td>{ formatDate(r.rect_date) ?? r.fee_date ?? '—'}</td>

                    {/* Verification status */}
                    <td className="text-center">
                      {r.verified === 1 ? (
                        <Badge className="badge-sm py-1 text-white" color="success" pill>
                          <i className="bi bi-check-circle mr-1" />Verified
                        </Badge>
                      ) : (
                        <Badge className="badge-sm py-1" color="warning" pill>
                          <i className="bi bi-clock mr-1" />Pending
                        </Badge>
                      )}
                    </td>

                    <td className="small"><small>{r.verified_by_user || <span className="text-muted">—</span>}</small></td>
                    <td><small>{r.verification_code ?? '—'}</small></td>

                    {/* Remark preview */}
                    <td>
                      {r.verify_remark ? (
                        <span
                          className="text-muted small"
                          title={r.verify_remark}
                          style={{ maxWidth: 120, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
                        >
                          {r.verify_remark}
                        </span>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {/* Totals footer row */}
            {receipts.length > 0 && !loading && (
              <tfoot>
                <tr className="font-weight-bold bg-light">
                  <td colSpan="4" className="text-right">Totals</td>
                  <td className="text-right">{fmt(displayedTotal)}</td>
                  <td colSpan="7" />
                </tr>
              </tfoot>
            )}
          </Table>
        </div>
      </Card>

      {/* Remark Modal */}
      <Modal isOpen={remarkModal} toggle={() => setRemarkModal(false)} centered size="md">
        <ModalHeader toggle={() => setRemarkModal(false)}>
          <i className="bi bi-comment-alt mr-2 text-secondary" />
          Verification Remark
        </ModalHeader>
        <ModalBody>
          {activeReceipt && (
            <p className="text-muted small mb-2">
              Receipt: <strong>{activeReceipt.chq_no ?? activeReceipt.receiptNo ?? "N/A"}</strong>
              &nbsp;|&nbsp;Student: <strong>{activeReceipt.sname1 ?? activeReceipt.stud_name}</strong>
              &nbsp;|&nbsp;Amount: <strong>{fmt(activeReceipt.amt_rec ?? activeReceipt.total_amount)}</strong>
            </p>
          )}
          <Select
            options={remarkOptions}
            value={remarkOptions.find((o) => o.value === selectedRemark) || null}
            placeholder="Select Remark"
            onChange={(opt) => setSelectedRemark(opt?.value || '')}
          />
          <Input
            className="mt-2"
            type="textarea"
            rows={4}
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            placeholder="Enter additional remarks..."
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRemarkModal(false)}>
            <i className="bi bi-times mr-1" />Cancel
          </Button>
          <Button color="primary" onClick={saveRemark}>
            <i className="bi bi-check-circle mr-1" />Save
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default FeeVerification;